import {
  LazyExoticComponent,
  Suspense,
  createContext,
  lazy,
  useContext,
} from "react";
import {
  Navigate,
  Outlet,
  RouterHistory,
  createBrowserHistory,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { Toaster } from "sonner";
import ReloadPrompt from "./common/ReloadPrompt";

const CreateAiPage = lazy(() => import("./pages/CreateAiPage"));
const SharedVideoPage = lazy(() => import("./pages/SharedVideoPage"));
const TeamInvitationPage = lazy(() => import("./pages/TeamInvitationPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AppLayout = lazy(() => import("./layouts/AppLayout"));
const NavigationLayout = lazy(() => import("./layouts/NavigationLayout"));
const OnboardingLayout = lazy(() => import("./layouts/OnboardingLayout"));
const PrivateRoutesLayout = lazy(() => import("./layouts/PrivateRoutesLayout"));
const SubscribeToProPage = lazy(() => import("./pages/SubscribeToProPage"));
const SubscribeToTeamsPage = lazy(() => import("./pages/SubscribeToTeamsPage"));
const ManageSubscriptionPage = lazy(
  () => import("./pages/ManageSubscriptionPage"),
);
const TemplatesPage = lazy(() => import("./pages/TemplatesPage"));
const MyProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const BrandKitPage = lazy(() => import("./pages/BrandKitPage"));
const LoginLayout = lazy(() => import("./layouts/LoginLayout"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));

export type AppLayoutComponent = (props: {
  children: JSX.Element | JSX.Element[];
}) => JSX.Element | null;

export interface AppRouterContext {
  layout: LazyExoticComponent<AppLayoutComponent> | AppLayoutComponent;
  forceLogin: boolean;
}

/**
 * Plain React context instead of TanStack router context: route components
 * must not use Register-typed hooks, or the router registration below
 * becomes circular and TypeScript's resolution of it order-dependent.
 */
export const AppConfigContext = createContext<AppRouterContext>({
  layout: AppLayout,
  forceLogin: false,
});

function RootComponent() {
  const { layout: Layout } = useContext(AppConfigContext);
  return (
    <Suspense fallback={null}>
      <Layout>
        <Outlet />
        <Toaster position="top-right" />
        <ReloadPrompt />
      </Layout>
    </Suspense>
  );
}

const rootRoute = createRootRoute({
  component: RootComponent,
  notFoundComponent: () => <Navigate to="/" replace />,
});

const privateRoutes = createRoute({
  getParentRoute: () => rootRoute,
  id: "private",
  component: function Private() {
    const { forceLogin } = useContext(AppConfigContext);
    return <PrivateRoutesLayout forceLogin={forceLogin} />;
  },
});

const navigationRoutes = createRoute({
  getParentRoute: () => privateRoutes,
  id: "navigation",
  component: NavigationLayout,
});

const onboardingRoutes = createRoute({
  getParentRoute: () => privateRoutes,
  id: "onboarding",
  component: OnboardingLayout,
});

const loginRoutes = createRoute({
  getParentRoute: () => rootRoute,
  id: "login-layout",
  component: LoginLayout,
});

const routeTree = rootRoute.addChildren([
  privateRoutes.addChildren([
    navigationRoutes.addChildren([
      createRoute({
        getParentRoute: () => navigationRoutes,
        path: "/",
        component: CreateAiPage,
      }),
      createRoute({
        getParentRoute: () => navigationRoutes,
        path: "/templates",
        component: TemplatesPage,
      }),
      createRoute({
        getParentRoute: () => navigationRoutes,
        path: "/my-projects",
        component: MyProjectsPage,
      }),
      createRoute({
        getParentRoute: () => navigationRoutes,
        path: "/brand-kit",
        component: BrandKitPage,
      }),
      createRoute({
        getParentRoute: () => navigationRoutes,
        path: "/invite/$id",
        component: () => <TeamInvitationPage sendFullUrl />,
      }),
      createRoute({
        getParentRoute: () => navigationRoutes,
        path: "/team-invitation/$id",
        component: TeamInvitationPage,
      }),
      createRoute({
        getParentRoute: () => navigationRoutes,
        path: "/settings/$tab",
        component: SettingsPage,
      }),
    ]),
    onboardingRoutes.addChildren([
      createRoute({
        getParentRoute: () => onboardingRoutes,
        path: "/manage-subscription",
        component: ManageSubscriptionPage,
      }),
      createRoute({
        getParentRoute: () => onboardingRoutes,
        path: "/subscribe/{-$status}",
        component: SubscribeToProPage,
      }),
      createRoute({
        getParentRoute: () => onboardingRoutes,
        path: "/subscribe-teams/{-$status}",
        component: SubscribeToTeamsPage,
      }),
    ]),
  ]),
  loginRoutes.addChildren([
    createRoute({
      getParentRoute: () => loginRoutes,
      path: "/login",
      component: LoginPage,
    }),
    createRoute({
      getParentRoute: () => loginRoutes,
      path: "/teams-login",
      component: () => <LoginPage isTeams />,
    }),
    createRoute({
      getParentRoute: () => loginRoutes,
      path: "/reset",
      component: ResetPasswordPage,
    }),
  ]),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/v/$exportId",
    component: SharedVideoPage,
  }),
]);

export function createAppRouter({ history }: { history?: RouterHistory } = {}) {
  return createRouter({
    routeTree,
    history: history ?? createBrowserHistory(),
    scrollRestoration: true,
    defaultPreload: "intent",
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
